import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const AccessibilityWidget: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(100);
  const [letterSpacing, setLetterSpacing] = useState(0);
  const [lineHeight, setLineHeight] = useState(0);
  const [highContrast, setHighContrast] = useState(false);
  const [dyslexiaFont, setDyslexiaFont] = useState(false);
  const [highlightLinks, setHighlightLinks] = useState(false);
  const [hideImages, setHideImages] = useState(false);
  const [stopAnimations, setStopAnimations] = useState(false);
  const [bigCursor, setBigCursor] = useState(false);
  const [readingGuide, setReadingGuide] = useState(false);

  // Drag state
  const [pos, setPos] = useState({ x: 20, y: 24 });
  const didDrag = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const posStartRef = useRef({ x: 0, y: 0 });
  const posRef = useRef(pos);
  posRef.current = pos;

  const startDrag = (startX: number, startY: number) => {
    didDrag.current = false;
    dragStartRef.current = { x: startX, y: startY };
    posStartRef.current = { ...posRef.current };
  };

  const moveDrag = (clientX: number, clientY: number) => {
    const dx = clientX - dragStartRef.current.x;
    const dy = clientY - dragStartRef.current.y;
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) didDrag.current = true;
    if (!didDrag.current) return;
    const newX = Math.max(0, Math.min(window.innerWidth - 64, posStartRef.current.x - dx));
    const newY = Math.max(0, Math.min(window.innerHeight - 64, posStartRef.current.y - dy));
    setPos({ x: newX, y: newY });
  };

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    startDrag(e.clientX, e.clientY);
    const onMouseMove = (ev: MouseEvent) => moveDrag(ev.clientX, ev.clientY);
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    startDrag(touch.clientX, touch.clientY);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    moveDrag(touch.clientX, touch.clientY);
    if (didDrag.current) e.preventDefault();
  };

  const onTouchEnd = () => {
    // didDrag stays set so handleButtonClick knows not to toggle
  };

  const handleButtonClick = () => {
    if (!didDrag.current) setIsOpen(!isOpen);
  };

  useEffect(() => {
    document.documentElement.style.fontSize = `${fontSize}%`;
  }, [fontSize]);

  useEffect(() => {
    document.documentElement.style.letterSpacing = letterSpacing > 0 ? `${letterSpacing}px` : '';
  }, [letterSpacing]);

  useEffect(() => {
    document.documentElement.style.lineHeight = lineHeight > 0 ? `${1.5 + lineHeight * 0.25}` : '';
  }, [lineHeight]);

  useEffect(() => {
    const cl = document.documentElement.classList;
    highContrast ? cl.add('a11y-high-contrast') : cl.remove('a11y-high-contrast');
  }, [highContrast]);

  useEffect(() => {
    const cl = document.documentElement.classList;
    dyslexiaFont ? cl.add('a11y-dyslexia') : cl.remove('a11y-dyslexia');
  }, [dyslexiaFont]);

  useEffect(() => {
    const cl = document.documentElement.classList;
    highlightLinks ? cl.add('a11y-highlight-links') : cl.remove('a11y-highlight-links');
  }, [highlightLinks]);

  useEffect(() => {
    const cl = document.documentElement.classList;
    hideImages ? cl.add('a11y-hide-images') : cl.remove('a11y-hide-images');
  }, [hideImages]);

  useEffect(() => {
    const cl = document.documentElement.classList;
    stopAnimations ? cl.add('a11y-stop-animations') : cl.remove('a11y-stop-animations');
  }, [stopAnimations]);

  useEffect(() => {
    const cl = document.documentElement.classList;
    bigCursor ? cl.add('a11y-big-cursor') : cl.remove('a11y-big-cursor');
  }, [bigCursor]);

  useEffect(() => {
    if (!readingGuide) {
      const guide = document.getElementById('a11y-reading-guide');
      if (guide) guide.remove();
      return;
    }
    const guide = document.createElement('div');
    guide.id = 'a11y-reading-guide';
    guide.style.cssText = 'position:fixed;left:0;width:100%;height:12px;background:rgba(200,255,0,0.18);pointer-events:none;z-index:99999;transition:top 0.05s ease-out;';
    document.body.appendChild(guide);
    const move = (e: MouseEvent) => { guide.style.top = `${e.clientY - 6}px`; };
    window.addEventListener('mousemove', move);
    return () => {
      window.removeEventListener('mousemove', move);
      guide.remove();
    };
  }, [readingGuide]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setIsOpen(false);
  }, []);

  const resetAll = () => {
    setFontSize(100);
    setLetterSpacing(0);
    setLineHeight(0);
    setHighContrast(false);
    setDyslexiaFont(false);
    setHighlightLinks(false);
    setHideImages(false);
    setStopAnimations(false);
    setBigCursor(false);
    setReadingGuide(false);
  };

  const ToggleBtn = ({ active, onClick, label, icon }: { active: boolean; onClick: () => void; label: string; icon: string }) => (
    <button
      onClick={onClick}
      className={`flex items-center gap-2.5 w-full py-2.5 px-3 rounded-lg text-xs font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent-green ${
        active
          ? 'bg-accent-green text-black'
          : 'bg-[#1a1a1a] border border-[#2a2a2a] text-gray-300 hover:border-accent-green'
      }`}
      aria-pressed={active}
    >
      <span className="text-base">{icon}</span>
      {label}
    </button>
  );

  return (
    <div className="fixed z-[9999]" style={{ right: pos.x, bottom: pos.y }} onKeyDown={handleKeyDown}>
      {/* Floating button - large and visible, draggable */}
      <button
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={handleButtonClick}
        className="w-12 h-12 rounded-full bg-white shadow-[0_4px_20px_rgba(0,0,0,0.5)] flex items-center justify-center hover:shadow-[0_4px_25px_rgba(0,0,0,0.7)] focus:outline-none focus:ring-4 focus:ring-accent-green/50 cursor-grab active:cursor-grabbing select-none touch-none"
        aria-label={t('accessibility.title')}
        aria-expanded={isOpen}
        aria-controls="accessibility-panel"
      >
        <img
          src="/accessibility.png"
          alt=""
          aria-hidden="true"
          style={{ filter: 'contrast(2)' }}
          className="w-8 h-8 pointer-events-none"
        />
      </button>

      {/* Panel */}
      {isOpen && (
        <div
          id="accessibility-panel"
          role="dialog"
          aria-label={t('accessibility.title')}
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#444 #161616', right: pos.x, bottom: pos.y + 80 }}
          className="fixed w-80 max-h-[calc(100vh-220px)] overflow-y-scroll bg-[#161616] border border-[#333] rounded-2xl p-6 animate-slide-up shadow-[0_0_40px_rgba(0,0,0,0.8),0_0_80px_rgba(0,0,0,0.4)]"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2.5">
              <img src="/accessibility.png" alt="" aria-hidden="true" className="w-6 h-6 invert" />
              <h3 className="text-white font-bold text-base">{t('accessibility.title')}</h3>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-white transition-colors focus:outline-none"
              aria-label={t('accessibility.close')}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          {/* Font size */}
          <div className="mb-4">
            <label className="text-gray-400 text-xs font-medium block mb-2">{t('accessibility.fontSize')}</label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setFontSize(Math.max(80, fontSize - 10))}
                className="w-10 h-10 rounded-lg bg-[#1a1a1a] border border-[#2a2a2a] text-white text-sm font-bold flex items-center justify-center hover:border-accent-green focus:outline-none focus:ring-2 focus:ring-accent-green"
                aria-label={t('accessibility.decreaseFont')}
              >
                A−
              </button>
              <div className="flex-1 text-center">
                <span className="text-white text-sm font-semibold">{fontSize}%</span>
              </div>
              <button
                onClick={() => setFontSize(Math.min(150, fontSize + 10))}
                className="w-10 h-10 rounded-lg bg-[#1a1a1a] border border-[#2a2a2a] text-white text-base font-bold flex items-center justify-center hover:border-accent-green focus:outline-none focus:ring-2 focus:ring-accent-green"
                aria-label={t('accessibility.increaseFont')}
              >
                A+
              </button>
            </div>
          </div>

          {/* Letter spacing */}
          <div className="mb-4">
            <label className="text-gray-400 text-xs font-medium block mb-2">{t('accessibility.letterSpacing')}</label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLetterSpacing(Math.max(0, letterSpacing - 1))}
                className="w-10 h-10 rounded-lg bg-[#1a1a1a] border border-[#2a2a2a] text-white text-sm font-bold flex items-center justify-center hover:border-accent-green focus:outline-none focus:ring-2 focus:ring-accent-green"
                aria-label={t('accessibility.decrease')}
              >
                −
              </button>
              <div className="flex-1 text-center">
                <span className="text-white text-sm font-semibold">+{letterSpacing}px</span>
              </div>
              <button
                onClick={() => setLetterSpacing(Math.min(5, letterSpacing + 1))}
                className="w-10 h-10 rounded-lg bg-[#1a1a1a] border border-[#2a2a2a] text-white text-sm font-bold flex items-center justify-center hover:border-accent-green focus:outline-none focus:ring-2 focus:ring-accent-green"
                aria-label={t('accessibility.increase')}
              >
                +
              </button>
            </div>
          </div>

          {/* Line height */}
          <div className="mb-5">
            <label className="text-gray-400 text-xs font-medium block mb-2">{t('accessibility.lineHeight')}</label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLineHeight(Math.max(0, lineHeight - 1))}
                className="w-10 h-10 rounded-lg bg-[#1a1a1a] border border-[#2a2a2a] text-white text-sm font-bold flex items-center justify-center hover:border-accent-green focus:outline-none focus:ring-2 focus:ring-accent-green"
                aria-label={t('accessibility.decrease')}
              >
                −
              </button>
              <div className="flex-1 text-center">
                <span className="text-white text-sm font-semibold">+{lineHeight}</span>
              </div>
              <button
                onClick={() => setLineHeight(Math.min(4, lineHeight + 1))}
                className="w-10 h-10 rounded-lg bg-[#1a1a1a] border border-[#2a2a2a] text-white text-sm font-bold flex items-center justify-center hover:border-accent-green focus:outline-none focus:ring-2 focus:ring-accent-green"
                aria-label={t('accessibility.increase')}
              >
                +
              </button>
            </div>
          </div>

          {/* Toggle options grid */}
          <div className="space-y-2 mb-5">
            <ToggleBtn active={highContrast} onClick={() => setHighContrast(!highContrast)} label={t('accessibility.highContrast')} icon="🔲" />
            <ToggleBtn active={dyslexiaFont} onClick={() => setDyslexiaFont(!dyslexiaFont)} label={t('accessibility.dyslexiaFont')} icon="🔤" />
            <ToggleBtn active={highlightLinks} onClick={() => setHighlightLinks(!highlightLinks)} label={t('accessibility.highlightLinks')} icon="🔗" />
            <ToggleBtn active={hideImages} onClick={() => setHideImages(!hideImages)} label={t('accessibility.hideImages')} icon="🖼️" />
            <ToggleBtn active={stopAnimations} onClick={() => setStopAnimations(!stopAnimations)} label={t('accessibility.stopAnimations')} icon="⏸️" />
            <ToggleBtn active={bigCursor} onClick={() => setBigCursor(!bigCursor)} label={t('accessibility.bigCursor')} icon="🖱️" />
            <ToggleBtn active={readingGuide} onClick={() => setReadingGuide(!readingGuide)} label={t('accessibility.readingGuide')} icon="📖" />
          </div>

          {/* Reset */}
          <button
            onClick={resetAll}
            className="w-full py-2.5 px-3 rounded-lg text-xs font-medium text-gray-400 hover:text-white border border-[#2a2a2a] hover:border-red-500 hover:text-red-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent-green"
          >
            {t('accessibility.reset')}
          </button>
        </div>
      )}
    </div>
  );
};

export default AccessibilityWidget;
